import React, {
  createContext,
  ReactNode,
  useCallback,
  useState,
  Dispatch,
  SetStateAction,
} from "react";

import {
  CreateLocalTrackOptions,
  ConnectOptions,
  LocalAudioTrack,
  LocalVideoTrack,
  Room,
  LocalDataTrack,
} from "twilio-video";

import { useTranslation } from "react-i18next";
import { Comment } from "../../../models/comment";
import { Screenshot } from "../../../models/screenshot";
import { Task } from "../../../models/task";
import { VideoSurvey } from "../../../models/video.survey";
import { ErrorCallback } from "../../../types";
import { NotificationToast } from "../../Generic/Toast/NotificationToast";
import AttachVisibilityHandler from "./AttachVisibilityHandler/AttachVisibilityHandler";
import useHandleRoomDisconnection from "./useHandleRoomDisconnection/useHandleRoomDisconnection";
import useHandleTrackPublicationFailed from "./useHandleTrackPublicationFailed/useHandleTrackPublicationFailed";
import useLocalTracks from "./useLocalTracks/useLocalTracks";
import useRestartAudioTrackOnDeviceChange from "./useRestartAudioTrackOnDeviceChange/useRestartAudioTrackOnDeviceChange";
import useRoom from "./useRoom/useRoom";
import useScreenShareToggle from "./useScreenShareToggle/useScreenShareToggle";

/*
 *  The hooks used by the VideoProvider component are different than the hooks found in the 'hooks/' directory. The hooks
 *  in the 'hooks/' directory can be used anywhere in a video application, and they can be used any number of times.
 *  the hooks in the 'VideoProvider/' directory are intended to be used by the VideoProvider component only. Using these hooks
 *  elsewhere in the application may cause problems as these hooks should not be used more than once in an application.
 */

export interface IVideoContext {
  dataTrack: LocalDataTrack;
  room: Room | null;
  localTracks: (LocalAudioTrack | LocalVideoTrack)[];
  isConnecting: boolean;
  connect: (token: string) => Promise<void>;
  onError: ErrorCallback;
  getLocalVideoTrack: (newOptions?: CreateLocalTrackOptions) => Promise<LocalVideoTrack>;
  getLocalAudioTrack: (deviceId?: string) => Promise<LocalAudioTrack>;
  isAcquiringLocalTracks: boolean;
  removeLocalVideoTrack: () => void;
  isSharingScreen: boolean;
  toggleScreenShare: () => void;
  getAudioAndVideoTracks: () => Promise<void>;
  task?: Task;
  setTask: (task: Task) => void;
  videoSurvey?: VideoSurvey;
  setVideoSurvey: (videoSurvey: VideoSurvey) => void;
  taskIsValid: () => boolean;
  participantsRequestedAccess: Array<string>;
  setParticipantsRequestedAccess: (participants: Array<string>) => void;
  participantsAllowed: Array<string>;
  setParticipantsAllowed: (participants: Array<string>) => void;
  isFinished: boolean;
  setIsFinished: (value: boolean) => void;
  screenshots: Screenshot[];
  setScreenshots: Dispatch<SetStateAction<Screenshot[]>>;
  messages: Comment[];
  setMessages: Dispatch<SetStateAction<Comment[]>>;
}

export const VideoContext = createContext<IVideoContext>(null!);

interface VideoProviderProps {
  options?: ConnectOptions;
  onError: ErrorCallback;
  children: ReactNode;
}

export function VideoProvider({ options, children, onError = () => {} }: VideoProviderProps) {
  const { t: translate } = useTranslation();
  const onErrorCallback: ErrorCallback = useCallback(
    (error: any) => {
      console.log(`ERROR: ${error.message}`, error);
      onError(error);
    },
    [onError]
  );

  const taskIsValid = () => {
    if (!task || !task?.id) {
      NotificationToast.error(translate("anUnknownErrorHasOccurred"));

      return false;
    }

    return true;
  };

  const [task, setTask] = useState<Task>();
  const [videoSurvey, setVideoSurvey] = useState<VideoSurvey>();
  const [isFinished, setIsFinished] = useState<boolean>(false);

  const [screenshots, setScreenshots] = useState<Screenshot[]>([]);
  const [messages, setMessages] = useState<Comment[]>([]);

  const {
    localTracks,
    getLocalVideoTrack,
    getLocalAudioTrack,
    isAcquiringLocalTracks,
    removeLocalAudioTrack,
    removeLocalVideoTrack,
    getAudioAndVideoTracks,
  } = useLocalTracks();

  const { room, isConnecting, connect, dataTrack } = useRoom(localTracks, onErrorCallback, options);

  const [isSharingScreen, toggleScreenShare] = useScreenShareToggle(room, onError);

  const [participantsAllowed, setParticipantsAllowed] = useState<Array<string>>([]);

  const [participantsRequestedAccess, setParticipantsRequestedAccess] = useState<Array<string>>([]);

  // Register callback functions to be called on room disconnect.
  useHandleRoomDisconnection(
    room,
    onError,
    removeLocalAudioTrack,
    removeLocalVideoTrack,
    isSharingScreen,
    toggleScreenShare
  );
  useHandleTrackPublicationFailed(room, onError);
  useRestartAudioTrackOnDeviceChange(localTracks);

  return (
    <VideoContext.Provider
      value={{
        isFinished,
        setIsFinished,
        participantsAllowed,
        setParticipantsAllowed,
        participantsRequestedAccess,
        setParticipantsRequestedAccess,
        taskIsValid,
        dataTrack,
        room,
        localTracks,
        isConnecting,
        onError: onErrorCallback,
        getLocalVideoTrack,
        getLocalAudioTrack,
        connect,
        isAcquiringLocalTracks,
        removeLocalVideoTrack,
        isSharingScreen,
        toggleScreenShare,
        getAudioAndVideoTracks,
        task,
        setTask,
        videoSurvey,
        setVideoSurvey,
        screenshots,
        setScreenshots,
        messages,
        setMessages,
      }}
    >
      {children}
      {/* 
        The AttachVisibilityHandler component is using the useLocalVideoToggle hook
        which must be used within the VideoContext Provider.
      */}
      <AttachVisibilityHandler />
    </VideoContext.Provider>
  );
}
